import React, { forwardRef, useImperativeHandle } from "react";
import { Box, Typography } from "@mui/material";
import ReactGantt  from "@dhx/react-gantt";
import "@dhx/react-gantt/dist/react-gantt.css";
import { useTheme } from "../../../shared/context";
import {
  ProjectForGantt,
} from "../utils/ganttTransformers";
import { useGantt } from "../hooks/useGantt";

export type ZoomLevel = "day" | "week" | "month" | "year";

interface ProjectsGanttViewProps {
  projects: ProjectForGantt[];
  zoomLevel?: ZoomLevel;
  onProjectClick?: (projectId: number, projectName: string) => void;
}

type GanttRef = {
  expandAll: () => void;
  collapseAll: () => void;
};

export const ProjectsGanttView = forwardRef<GanttRef, ProjectsGanttViewProps>(
  (
    { projects, zoomLevel = "month", onProjectClick, }: ProjectsGanttViewProps , 
    ref: React.Ref<GanttRef>
  ) => {
  const { mode } = useTheme();
  const { ganttRef, tasks, expandAll, collapseAll } = useGantt(projects, zoomLevel, onProjectClick);

  useImperativeHandle(ref, () => ({
    expandAll,
    collapseAll,
  }));

  const ganttTheme = mode === "dark" ? "dark" : "terrace";

  if (tasks.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Typography color="text.secondary">
          No projects with schedule data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" sx={{ "& .gantt_container": { height: "100%" } }}>
      <style>{`
        
        .gantt_icon_phase,
        .gantt_icon_activity {
          background-repeat: no-repeat;
          background-position: center center;
          background-size: 18px 18px;
        }

        .gantt_icon_phase {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23E84B0F'%3E%3Cpath d='M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z'/%3E%3C/svg%3E");
        }

        .gantt_icon_activity {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232196F3'%3E%3Cpath d='M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z'/%3E%3C/svg%3E");
        }

      `}</style>
      <ReactGantt
        ref={ganttRef}
        tasks={tasks}
        links={[]}
        config={{
          scales: [
            { unit: "month", step: 1, format: "%M %Y" },
            { unit: "day", step: 1, format: "%d %M" },
          ],
          columns: [
            { name: "text", label: "Project", tree: true, width: 200 },
            {
              name: "start_date",
              label: "Planned Start",
              align: "center",
              width: 130,
            },
            {
              name: "end_date",
              label: "Planned End",
              align: "center",
              width: 130,
            },
            {
              name: "view",
              label: "",
              align: "center",
              width: 40,
              template: (task: { id: string }) => {
                if (String(task.id).startsWith("project_")) {
                  return `<span class="gantt-view-icon" data-project-id="${task.id}" style="cursor:pointer;display:inline-flex;align-items:center;justify-content:center;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </span>`;
                }
                return "";
              },
            },
          ],
          scale_height: 60,
          grid_width: 520,
          drag_progress: false,
          drag_resize: false,
          drag_move: false,
          order_branch: false,
          fit_tasks: true,
          show_empty_state: true,
          readonly: true,
        }}
        plugins={{ tooltip: true }}
        theme={ganttTheme}
        onBeforeLightbox={() => false}
      />
    </Box>
  );
}
);
